// File: src/components/Maintenance/MaintenanceManagementBoard.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const safeArray = (v) =>
  Array.isArray(v)
    ? v
    : Array.isArray(v?.items)
      ? v.items
      : Array.isArray(v?.data)
        ? v.data
        : Array.isArray(v?.vendors)
          ? v.vendors
          : Array.isArray(v?.assets)
            ? v.assets
            : Array.isArray(v?.staff)
              ? v.staff
              : [];

const STATUS_OPTIONS = [
  "all",
  "Open",
  "Submitted",
  "Under Review",
  "Approved",
  "Assigned",
  "In Progress",
  "On Hold",
  "Waiting Parts",
  "Waiting Vendor",
  "Completed",
  "Closed",
  "Cancelled",
];

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n)
    ? new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)
    : "£0.00";
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();

  if (["closed", "completed"].includes(s)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (["assigned", "approved", "in progress"].includes(s)) {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }

  if (["waiting parts", "waiting vendor", "on hold"].includes(s)) {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }

  if (["submitted", "under review"].includes(s)) {
    return "border-violet-200 bg-violet-50 text-violet-700";
  }

  if (["cancelled"].includes(s)) {
    return "border-slate-200 bg-slate-100 text-slate-700";
  }

  return "border-rose-200 bg-rose-50 text-rose-700";
};

const priorityBadgeClass = (priority) => {
  const p = String(priority || "").toLowerCase();

  if (p === "high") return "border-rose-200 bg-rose-50 text-rose-700";
  if (p === "medium") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-slate-200 bg-slate-100 text-slate-700";
};

const Card = ({ title, subtitle, rightSlot, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {rightSlot ? <div>{rightSlot}</div> : null}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InfoTile = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-1 break-words text-sm font-medium text-slate-900">
      {value ?? "—"}
    </div>
  </div>
);

const MaintenanceManagementBoard = () => {
  const navigate = useNavigate();

  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const queueStats = useMemo(() => {
    let open = 0;
    let inProgress = 0;
    let waiting = 0;
    let closed = 0;

    for (const row of queue) {
      const s = String(row?.status || "").toLowerCase();

      if (["open", "submitted", "under review", "approved", "assigned"].includes(s)) {
        open += 1;
      } else if (["in progress"].includes(s)) {
        inProgress += 1;
      } else if (["on hold", "waiting parts", "waiting vendor"].includes(s)) {
        waiting += 1;
      } else if (["closed", "completed", "cancelled"].includes(s)) {
        closed += 1;
      }
    }

    return { open, inProgress, waiting, closed };
  }, [queue]);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await api.get("/solo-mdsh-api/maintenance/management/queue", {
        params: {
          q: search.trim() || undefined,
          status: status === "all" ? undefined : status,
        },
      });

      setQueue(safeArray(data));
    } catch (err) {
      console.error("Failed to load maintenance queue:", err);
      toast.error("Failed to load maintenance queue");
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleSearch = async () => {
    await fetchQueue();
  };

  const handleOpenTask = (taskId) => {
    if (!taskId) return;
    navigate(`/maintenance/manage/${taskId}`);
  };

  return (
    <div className="min-h-full bg-slate-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-[1700px] space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-cyan-100 bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-700">
                Maintenance Management Workspace
              </div>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">
                Manage maintenance queue
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Review maintenance tasks that require management action, then open a dedicated task
                screen to assign staff, link assets, link purchase orders, record vendor activity,
                add cost lines, and close work professionally.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <InfoTile label="Open Pipeline" value={queueStats.open} />
              <InfoTile label="In Progress" value={queueStats.inProgress} />
              <InfoTile label="Waiting" value={queueStats.waiting} />
              <InfoTile label="Closed" value={queueStats.closed} />
            </div>
          </div>
        </div>

        <Card
          title="Management Queue"
          subtitle="Filter and review maintenance tasks that require management action."
          rightSlot={
            <Badge className="border border-blue-200 bg-blue-50 text-blue-700">
              Click a row to open task screen
            </Badge>
          }
        >
          <div className="mb-5 flex flex-col gap-3 lg:flex-row">
            <input
              type="text"
              placeholder="Search issue, task, vendor, PO, asset, location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              className="h-11 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              {STATUS_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item === "all" ? "All statuses" : item}
                </option>
              ))}
            </select>

            <Button
              onClick={handleSearch}
              className="h-11 rounded-xl bg-slate-900 px-5 text-white hover:bg-slate-800"
            >
              Search
            </Button>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <div className="overflow-auto">
              <table className="min-w-[1180px] w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Task</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-left font-semibold">Priority</th>
                    <th className="px-4 py-3 text-left font-semibold">Urgency</th>
                    <th className="px-4 py-3 text-left font-semibold">Asset</th>
                    <th className="px-4 py-3 text-left font-semibold">Location</th>
                    <th className="px-4 py-3 text-left font-semibold">Vendor</th>
                    <th className="px-4 py-3 text-left font-semibold">PO</th>
                    <th className="px-4 py-3 text-right font-semibold">Actual Cost</th>
                    <th className="px-4 py-3 text-left font-semibold">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-10 text-center text-slate-500">
                        Loading management queue...
                      </td>
                    </tr>
                  ) : queue.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-10 text-center text-slate-500">
                        No maintenance tasks found.
                      </td>
                    </tr>
                  ) : (
                    queue.map((task) => (
                      <tr
                        key={task.task_id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleOpenTask(task.task_id)}
                      >
                        <td className="px-4 py-4 align-top">
                          <div className="font-semibold text-slate-900 transition hover:text-blue-700">
                            {task.issue_type || "—"}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {task.task_number || `MT-${task.task_id}`}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            Created {formatDateTime(task.created_at)}
                          </div>
                        </td>

                        <td className="px-4 py-4 align-top">
                          <Badge className={`border ${statusBadgeClass(task.status)}`}>
                            {task.status || "—"}
                          </Badge>
                        </td>

                        <td className="px-4 py-4 align-top">
                          <Badge className={`border ${priorityBadgeClass(task.priority)}`}>
                            {task.priority || "—"}
                          </Badge>
                        </td>

                        <td className="px-4 py-4 align-top text-slate-700">
                          {task.urgency_level || "—"}
                        </td>

                        <td className="px-4 py-4 align-top text-slate-700">
                          {task.asset_number || "—"}
                        </td>

                        <td className="px-4 py-4 align-top text-slate-700">
                          {task.location || "—"}
                        </td>

                        <td className="px-4 py-4 align-top text-slate-700">
                          {task.vendor_name || "—"}
                        </td>

                        <td className="px-4 py-4 align-top text-slate-700">
                          {task.po_number || "—"}
                        </td>

                        <td className="px-4 py-4 text-right align-top font-medium text-slate-900">
                          {formatMoney(task.actual_cost)}
                        </td>

                        <td className="px-4 py-4 align-top">
                          <Button
                            type="button"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenTask(task.task_id);
                            }}
                            className="bg-slate-900 text-white hover:bg-slate-800"
                          >
                            Manage
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MaintenanceManagementBoard;